import type { ChartData, StrategyResult, BreakdownData } from "../types";

/**
 * Get chart colors from CSS variables defined in @theme
 * These colors are used for pie charts and other data visualizations
 */
function getChartColors(): string[] {
  const fallbackColors = ["#6f72af", "#912b88", "#d53880", "#f499be", "#f47738", "#b58840", "#85994b", "#28a197"];

  if (typeof window === 'undefined') {
    return fallbackColors;
  }

  const style = getComputedStyle(document.documentElement);
  const colors = [
    style.getPropertyValue('--color-chart-1').trim(),
    style.getPropertyValue('--color-chart-2').trim(),
    style.getPropertyValue('--color-chart-3').trim(),
    style.getPropertyValue('--color-chart-4').trim(),
    style.getPropertyValue('--color-chart-5').trim(),
    style.getPropertyValue('--color-chart-6').trim(),
    style.getPropertyValue('--color-chart-7').trim(),
    style.getPropertyValue('--color-chart-8').trim(),
  ];

  // If CSS variables aren't loaded yet, fall back to hardcoded colors
  if (colors[0] === '') {
    return fallbackColors;
  }

  return colors;
}

// Lazily initialize colors when first accessed
class ChartColours {
  private colors: string[] | null = null;

  private getColors(): string[] {
    if (!this.colors) {
      this.colors = getChartColors();
    }
    return this.colors;
  }

  // Proxy array access
  get length(): number {
    return this.getColors().length;
  }

  [index: number]: string;

  // Make it iterable and support array indexing
  [Symbol.iterator](): Iterator<string> {
    return this.getColors()[Symbol.iterator]();
  }
}

// Create proxy to intercept array access
const chartColoursInstance = new ChartColours();
export const CHART_COLOURS = new Proxy(chartColoursInstance, {
  get(target: ChartColours, prop: string | symbol): string | number | (() => Iterator<string>) | undefined {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const colors = (target as any).getColors() as string[];
    if (typeof prop === 'string' && !isNaN(Number(prop))) {
      return colors[Number(prop)];
    }
    if (prop === 'length') {
      return colors.length;
    }
    if (prop === Symbol.iterator) {
      return () => colors[Symbol.iterator]();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (colors as any)[prop] as string | undefined;
  }
}) as unknown as string[];

/**
 * Generate marginal tax rate data per bracket of size `step` up to `maxIncome`.
 * UK 2024/25 rates:
 *   0% up to £12,570
 *   20% on £12,571–£50,270
 *   40% on £50,271–£100,000
 *   60% on £100,000–£125,140
 *   45% above £125,140
 */
export function generateTaxBurdenData(step: number, maxIncome: number): ChartData[] {
  const data: ChartData[] = [];
  for (let income = step; income <= maxIncome; income += step) {
    let rate: number;
    if (income <= 12570) rate = 0;
    else if (income <= 50270) rate = 20;
    else if (income <= 100000) rate = 40;
    else if (income <= 125140) rate = 60;
    else rate = 45;
    data.push({ income, rate });
  }
  return data;
}

/**
 * Map each strategy into a simple { name, value } for the pie chart.
 * Here we use cash_in_hand as the slice value, but you can change to any field.
 * Note: Strategies with null names (like baseline) are converted to "Baseline" string.
 */
export function mapToBreakdownData(
  strategies: StrategyResult[],
): BreakdownData[] {
  return strategies.map((s) => ({
    name: s.name ?? 'Baseline',
    value: s.cash_in_hand,
  }));
}
